import {
  Button,
  BuyTokenDropDownSelectBox,
  Section,
  SellWalletTokenOrUnderlyingYieldBearingAssetAmountInputs,
  SellWalletTokenOrUnderlyingYieldBearingAssetDropDownSelectBox,
  WalletFoundButNoAssetsFoundToSellWarningAlert,
} from '@/components';
import { useWalletsContext } from '@/components/providers';
import APP_CONFIG from '@/config/app';
import useSmoothScroll from '@/lib/hooks/useSmoothScroll';
import { formatNumberToAmountInTokenCurrencyString } from '@/utils/formatUtils';
import { TRADE_CARD } from '@/utils/strings';
import React, { FC, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  FormProvider as ReactHookFormProvider,
  useForm,
} from 'react-hook-form';
import {
  BuyableAssetType,
  SellableAssetType,
  TradeFormDataInterface,
} from './interfaces/TradeInterfaces';
const { MAIN_PADDING, MAIN_GAP } = APP_CONFIG.STYLES;
const spacingConfig = {
  padding: `p${MAIN_PADDING}`,
  gap: `gap${MAIN_GAP}`,
};
const CROSS_CHAIN_TRADE_BLOCKCHAINS = ['ethereum', 'base'];
const normalizeBlockchain = (blockchain?: string | null) =>
  typeof blockchain === 'string' ? blockchain.toLowerCase() : '';
interface Props {
  tradeData: TradeFormDataInterface;
  isLoading: boolean;
  setTradeData: React.Dispatch<React.SetStateAction<TradeFormDataInterface>>;
  handleSuccessfulTradeConfiguration: () => void;
}
const TradeForm: FC<Props> = ({
  tradeData,
  isLoading = false,
  setTradeData,
  handleSuccessfulTradeConfiguration:
    handleSuccessfulTradeConfigurationConfirmation,
}) => {
  const {
    getTokensWithPositiveBalance,
    getUnderlyingYieldBearingAssetsWithPositiveBalance,
  } = useWalletsContext();
  const hasSellableAssets = useMemo(() => {
    const tokens = getTokensWithPositiveBalance(
      APP_CONFIG.TRADE_SUPPORTED_NETWORKS,
    );
    const yieldBearingAssets =
      getUnderlyingYieldBearingAssetsWithPositiveBalance(
        APP_CONFIG.TRADE_SUPPORTED_NETWORKS,
      );
    return [...tokens, ...yieldBearingAssets].length > 0;
  }, [
    getTokensWithPositiveBalance,
    getUnderlyingYieldBearingAssetsWithPositiveBalance,
  ]);
  const { smoothScrollTo } = useSmoothScroll();
  const formContainerRef = useRef<HTMLDivElement>(null);
  const methods = useForm({
    defaultValues: {
      amount: tradeData.amount !== 0 ? tradeData.amount.toString() : '',
      sellAsset: tradeData.sellAsset || null,
      buyAsset: tradeData.buyAsset || null,
    },
  });
  const {
    register,
    setValue,
    formState: { errors },
    clearErrors,
    setError,
    watch,
    handleSubmit,
    reset,
  } = methods;
  const amount = watch('amount') || '';
  const sellAsset = watch('sellAsset');
  const buyAsset = watch('buyAsset');
  useEffect(() => {
    reset({
      amount: tradeData.amount !== 0 ? tradeData.amount.toString() : '',
      sellAsset: tradeData.sellAsset || null,
      buyAsset: tradeData.buyAsset || null,
    });
    if (!isLoading && tradeData.buyAsset) {
      setValue('buyAsset', tradeData.buyAsset, {
        shouldValidate: false,
        shouldDirty: false,
      });
    }
  }, [tradeData, isLoading, reset, setValue]);
  const handleBuyAssetChange = useCallback(
    (selectedToken: BuyableAssetType) => {
      setValue('buyAsset', selectedToken, {
        shouldValidate: false,
        shouldDirty: false,
      });
      setTradeData((prevData) => ({
        ...prevData,
        buyAsset: selectedToken,
      }));
    },
    [setValue, setTradeData],
  );
  const handleSellAssetChange = useCallback(
    (selectedAsset: SellableAssetType | null) => {
      if (selectedAsset) {
        const maxAmount = selectedAsset.balance;
        const defaultAmount =
          maxAmount * APP_CONFIG.ASSET_TO_SELL_DEFAULT_PERCENTAGE;
        const allowedBuyBlockchains = CROSS_CHAIN_TRADE_BLOCKCHAINS.includes(
          normalizeBlockchain(selectedAsset.blockchain),
        )
          ? CROSS_CHAIN_TRADE_BLOCKCHAINS
          : [normalizeBlockchain(selectedAsset.blockchain)].filter(Boolean);
        const shouldResetBuyAsset =
          buyAsset &&
          !allowedBuyBlockchains.includes(
            normalizeBlockchain(buyAsset.blockchain),
          );
        setValue('sellAsset', selectedAsset);
        setValue('amount', defaultAmount.toString());
        if (shouldResetBuyAsset) {
          setValue('buyAsset', null);
        }
        setTradeData((prevData) => ({
          ...prevData,
          sellAsset: selectedAsset,
          buyAsset: shouldResetBuyAsset ? null : prevData.buyAsset,
          amount: defaultAmount,
        }));
      } else {
        setValue('sellAsset', null);
        setValue('amount', '');
        setTradeData((prevData) => ({
          ...prevData,
          sellAsset: null,
          amount: 0,
        }));
      }
    },
    [buyAsset, setValue, setTradeData],
  );
  const handleMaxClick = useCallback(() => {
    if (tradeData.sellAsset) {
      const maxAmount = tradeData.sellAsset.balance;
      setValue('amount', maxAmount.toString());
    }
  }, [tradeData.sellAsset, setValue]);
  const showErrorAfterScroll = useCallback(
    (field: 'buyAsset' | 'sellAsset' | 'amount', message: string) => {
      const formContainerElement = formContainerRef.current;
      if (formContainerElement) {
        smoothScrollTo(formContainerElement, 300, () => {
          setTimeout(() => {
            setError(field, {
              type: 'manual',
              message,
            });
          }, 50);
        });
      }
    },
    [setError, smoothScrollTo],
  );
  const getMaxAmountForSelectedToken = useCallback((): number => {
    return tradeData.sellAsset ? tradeData.sellAsset.balance : 0;
  }, [tradeData.sellAsset]);
  const validateAmount = useCallback(
    (value: string) => {
      const maxAmount = getMaxAmountForSelectedToken();
      const amountValue = parseFloat(value || '0');
      if (isNaN(amountValue) || amountValue <= 0 || amountValue > maxAmount) {
        if (amountValue <= 0) {
          return TRADE_CARD.SCREENS.TRADE.MESSAGES.VALIDATION.INVALID_AMOUNT;
        }
        if (amountValue > maxAmount) {
          return TRADE_CARD.SCREENS.TRADE.MESSAGES.VALIDATION.AMOUNT_TRADED_FIELD_LESS_THAN_AVAILABLE_ERROR_MESSAGE.replace(
            '[available_funds_in_token_currency]',
            formatNumberToAmountInTokenCurrencyString(
              maxAmount,
              tradeData.sellAsset?.symbol || '',
            ),
          );
        }
      }
      return true;
    },
    [getMaxAmountForSelectedToken, tradeData.sellAsset],
  );
  const onSubmit = useCallback(
    async (data: any) => {
      const maxAmount = getMaxAmountForSelectedToken();
      const amountValue = parseFloat(data.amount);
      if (!data.sellAsset) {
        showErrorAfterScroll(
          'sellAsset',
          TRADE_CARD.SCREENS.TRADE.MESSAGES.VALIDATION.SELL_ASSET_REQUIRED,
        );
        return;
      }
      if (!data.buyAsset) {
        showErrorAfterScroll(
          'buyAsset',
          TRADE_CARD.SCREENS.TRADE.MESSAGES.VALIDATION.BUY_ASSET_REQUIRED,
        );
        return;
      }
      const amountValidationMessage = validateAmount(data.amount);
      if (amountValidationMessage !== true) {
        showErrorAfterScroll('amount', amountValidationMessage as string);
        return;
      }
      setTradeData({
        ...tradeData,
        amount: amountValue,
      });
      handleSuccessfulTradeConfigurationConfirmation();
    },
    [
      getMaxAmountForSelectedToken,
      validateAmount,
      setTradeData,
      tradeData,
      showErrorAfterScroll,
      handleSuccessfulTradeConfigurationConfirmation,
    ],
  );
  return (
    <ReactHookFormProvider {...methods}>
      <div
        ref={formContainerRef}
        className={`flex flex-col ${spacingConfig.gap}`}
      >
        <Section
          headerTitle={TRADE_CARD.SCREENS.TRADE.LABELS.BUY_ASSET_SECTION_LABEL}
          body={
            <BuyTokenDropDownSelectBox
              selectedAsset={buyAsset}
              selectedSellBlockchain={sellAsset?.blockchain || null}
              handleSelectChange={handleBuyAssetChange}
              register={register}
              setValue={setValue}
              clearErrors={clearErrors}
              errors={errors}
            />
          }
        />
        <Section
          headerTitle={TRADE_CARD.SCREENS.TRADE.LABELS.SELL_ASSET_SECTION_LABEL}
          body={
            hasSellableAssets ? (
              <div className={`flex flex-col ${spacingConfig.gap}`}>
                <SellWalletTokenOrUnderlyingYieldBearingAssetDropDownSelectBox
                  selectedAsset={sellAsset}
                  handleSelectChange={handleSellAssetChange}
                  register={register}
                  setValue={setValue}
                  clearErrors={clearErrors}
                  errors={errors}
                  buyAsset={buyAsset}
                />
                <SellWalletTokenOrUnderlyingYieldBearingAssetAmountInputs
                  selectedAsset={sellAsset}
                  amountInTokenCurrency={amount}
                  handleMaxClick={handleMaxClick}
                  register={register}
                  setValue={setValue}
                  clearErrors={clearErrors}
                  errors={errors}
                />
              </div>
            ) : (
              <WalletFoundButNoAssetsFoundToSellWarningAlert />
            )
          }
        />
        {hasSellableAssets && (
          <Button
            fullWidth
            onClick={handleSubmit(onSubmit)}
            disabled={!hasSellableAssets}
          >
            {TRADE_CARD.SCREENS.TRADE.LABELS.TRADE_BUTTON}
          </Button>
        )}
      </div>
    </ReactHookFormProvider>
  );
};
export default React.memo(TradeForm);
